import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Skeleton,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import NavBar from "../../../Components/NavBar";
import {
  getConnectedAccount,
  connectWallet as triggerConnectWallet,
  subscribeToWalletEvents,
} from "../../../../static/walletUtils";
import {
  CommonInput,
  CommonSelect,
  CommonButton,
  NoData,
} from "../../../Components/ui";

import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import { DEFAULT_CURRENCY } from "../../../../static/Constants";

import SlotHeroBanner from "./components/SlotHeroBanner";
import SlotMachineCard from "./components/SlotMachineCard";

import "./SlotLottery.css";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "highestJackpot", label: "Highest Jackpot" },
  { value: "lowestBet", label: "Lowest Bet" },
];

const DUMMY_LOTTERIES = [
  {
    lotteryId: 101,
    creator: "AutoBET Official",
    jackpot: "0.850",
    betAmount: "0.010",
    currency: DEFAULT_CURRENCY,
    jackpotLevel: "Mega",
    players: 128,
  },
  {
    lotteryId: 102,
    creator: "AutoBET Official",
    jackpot: "0.420",
    betAmount: "0.005",
    currency: DEFAULT_CURRENCY,
    jackpotLevel: "Major",
    players: 76,
  },
  {
    lotteryId: 103,
    creator: "AutoBET Official",
    jackpot: "0.180",
    betAmount: "0.002",
    currency: DEFAULT_CURRENCY,
    jackpotLevel: "Mini",
    players: 43,
  },
  {
    lotteryId: 104,
    creator: "AutoBET Official",
    jackpot: "1.250",
    betAmount: "0.020",
    currency: DEFAULT_CURRENCY,
    jackpotLevel: "Grand",
    players: 210,
  },
  {
    lotteryId: 105,
    creator: "AutoBET Official",
    jackpot: "0.050",
    betAmount: "0.001",
    currency: DEFAULT_CURRENCY,
    jackpotLevel: "Micro",
    players: 15,
  },
  {
    lotteryId: 106,
    creator: "AutoBET Official",
    jackpot: "3.500",
    betAmount: "0.050",
    currency: DEFAULT_CURRENCY,
    jackpotLevel: "Super",
    players: 405,
  },
  {
    lotteryId: 107,
    creator: "AutoBET Official",
    jackpot: "0.999",
    betAmount: "0.015",
    currency: DEFAULT_CURRENCY,
    jackpotLevel: "Mega",
    players: 156,
  },
];

const SlotLottery = () => {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [account, setAccount] = useState("");

  const checkAccount = useCallback(async () => {
    const acc = await getConnectedAccount();
    setAccount(acc);
  }, []);

  const connectWallet = async () => {
    const res = await triggerConnectWallet();
    if (res.account) {
      setAccount(res.account);
    } else if (res.error === "NO_WALLET") {
      toast.error(res.message);
    } else if (res.isPending) {
      toast.info(res.message);
    } else if (!res.isCancelled && res.message) {
      toast.error(res.message);
    }
  };

  const combinedJackpot = useMemo(() => {
    return DUMMY_LOTTERIES.reduce(
      (total, lottery) => total + Number(lottery.jackpot),
      0
    ).toFixed(3);
  }, []);

  const filteredLotteries = useMemo(() => {
    let list = [...DUMMY_LOTTERIES];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      list = list.filter(
        (lottery) =>
          String(lottery.lotteryId).includes(query) ||
          lottery.creator.toLowerCase().includes(query) ||
          lottery.jackpotLevel.toLowerCase().includes(query)
      );
    }

    list.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.lotteryId - b.lotteryId;
        case "highestJackpot":
          return Number(b.jackpot) - Number(a.jackpot);
        case "lowestBet":
          return Number(a.betAmount) - Number(b.betAmount);
        case "newest":
        default:
          return b.lotteryId - a.lotteryId;
      }
    });

    return list;
  }, [searchQuery, sortBy]);

  const fetchSlotLotteries = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 800);
  };

  useEffect(() => {
    fetchSlotLotteries();
    checkAccount();

    const unsubscribe = subscribeToWalletEvents({
      onAccountsChanged: (acc) => setAccount(acc),
      onDisconnect: () => setAccount(""),
    });

    return unsubscribe;
  }, [checkAccount]);

  return (
    <Box
      sx={{
        position: "relative",
        overflowX: "hidden",
        minHeight: "100vh",
        bgcolor: colors.dashboard.canvas,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <NavBar />

      <Container
        maxWidth="xl"
        className="slot-page-container"
        sx={{ pt: { xs: "96px", sm: "108px", md: "116px" }, pb: 8, flexGrow: 1 }}
      >
        {/* Hero Header & Live Stats Banner */}
        <SlotHeroBanner
          totalActive={DUMMY_LOTTERIES.length}
          combinedPrize={combinedJackpot}
          currency={DEFAULT_CURRENCY}
        />

        {/* Filter, Search & Sorting Controls Bar */}
        <Box
          sx={{
            bgcolor: colors.dashboard.cardBg,
            borderRadius: borderRadiusPx.xl,
            border: `1px solid ${colors.dashboard.cardBorder}`,
            p: { xs: 2, md: 2.5 },
            mb: 4,
            boxShadow: customShadows.cardFlat,
          }}
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Search Field */}
            <Grid item xs={12} sm={6} md={5}>
              <CommonInput
                placeholder="Search by Machine #ID or Creator..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
                  </InputAdornment>
                }
                fullWidth
                size="small"
              />
            </Grid>

            {/* Sorting & Refresh */}
            <Grid item xs={12} sm={6} md={5}>
              <Stack
                direction="row"
                spacing={1.5}
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                alignItems="center"
              >
                <Box sx={{ minWidth: 180, flexGrow: { xs: 1, sm: 0 } }}>
                  <CommonSelect
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    options={SORT_OPTIONS}
                    size="small"
                  />
                </Box>
                <CommonButton
                  variant="outline"
                  size="small"
                  onClick={() => fetchSlotLotteries()}
                  disabled={loading}
                  sx={{ p: 1, minWidth: 40 }}
                >
                  <RefreshIcon
                    sx={{
                      fontSize: 18,
                      animation: loading ? "spin 1s linear infinite" : undefined,
                    }}
                  />
                </CommonButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Live Wheels Grid or Loading Skeletons */}
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3].map((idx) => (
              <Grid item xs={12} sm={6} lg={4} key={`skel-${idx}`}>
                <Box
                  sx={{
                    bgcolor: colors.dashboard.cardBg,
                    borderRadius: borderRadiusPx.xl,
                    border: `1px solid ${colors.dashboard.cardBorder}`,
                    p: 3,
                    height: 520,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="text" width="30%" height={16} />
                    </Box>
                  </Stack>
                  <Skeleton
                    variant="rounded"
                    height={220}
                    sx={{ mx: "auto", my: 2, width: "100%", borderRadius: borderRadiusPx.md }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={60}
                    sx={{ borderRadius: borderRadiusPx.md }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={44}
                    sx={{ borderRadius: borderRadiusPx.md }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : filteredLotteries.length === 0 ? (
          <Box sx={{ my: 3 }}>
            <NoData
              message={
                searchQuery
                  ? "No active SlotBET machines match your search criteria."
                  : "No active SlotBET machines currently open. Check back soon!"
              }
            />
          </Box>
        ) : (
          <Grid container spacing={3} alignItems="flex-start">
            {filteredLotteries.map((lottery) => (
              <Grid item xs={12} sm={6} lg={4} key={lottery.lotteryId}>
                <SlotMachineCard 
                  lotteryData={lottery} 
                  account={account}
                  onConnectWallet={connectWallet}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Game Rules & FAQ Drawer */}
        <Box sx={{ mt: 4 }}>
          <Accordion
            sx={{
              borderRadius: `${borderRadiusPx.lg} !important`,
              border: `1px solid ${colors.dashboard.cardBorder}`,
              boxShadow: "none",
              backgroundColor: colors.dashboard.cardBg,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <HelpIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: colors.slate[900] }}
                >
                  How does 3-SlotBET work?
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: colors.slate[50],
                      borderRadius: borderRadiusPx.md,
                      border: `1px solid ${colors.slate[200]}`,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: colors.primary.main, mb: 0.5 }}
                    >
                      1. Place Your Bet
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.slate[600], lineHeight: 1.6 }}
                    >
                      Choose your bet amount and start the 3-reel slot machine.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: colors.slate[50],
                      borderRadius: borderRadiusPx.md,
                      border: `1px solid ${colors.slate[200]}`,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: colors.primary.main, mb: 0.5 }}
                    >
                      2. Spin & Match
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.slate[600], lineHeight: 1.6 }}
                    >
                      Press SPIN. All 3 reels spin independently and stop with the symbols aligned on the middle payline.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: colors.slate[50],
                      borderRadius: borderRadiusPx.md,
                      border: `1px solid ${colors.slate[200]}`,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: colors.primary.main, mb: 0.5 }}
                    >
                      3. Match & Win
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.slate[600], lineHeight: 1.6 }}
                    >
                      Match all 3 symbols on the payline to win. Higher-value combinations give bigger rewards. If the symbols don't match, you lose that spin.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Container>
    </Box>
  );
};

export default SlotLottery;